import type { Route } from '@tuyau/core/types';
import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { AccountService } from '@core/auth';
import { TuyauService } from '@core/tuyau';

export type InvoiceTaskSummary =
  Route.Response<'employee_v2.property_management_invoices.my_tasks.summary'>['data'];

const EMPTY_SUMMARY: InvoiceTaskSummary = {
  total: 0,
  readyToday: 0,
  pastDue: 0,
  deferred: 0,
};

/**
 * My property-management invoice tasks summary: the dashboard's figures, and
 * the nav badge reads its `total` (no count endpoint). Reads nothing until the
 * account opens the area.
 */
@Injectable({ providedIn: 'root' })
export class InvoiceTaskSummaryService {
  private tuyau = inject(TuyauService);
  private account = inject(AccountService);
  private readonly fetchedAt = signal<Date | null>(null);

  private summaryResource = resource({
    params: () => ({ allowed: this.account.canAccessInvoices() }),
    defaultValue: EMPTY_SUMMARY,
    loader: async ({ params }) => {
      if (!params.allowed) return EMPTY_SUMMARY;

      const response =
        await this.tuyau.client.api.employeeV2.propertyManagementInvoices.myTasks.summary(
          {}
        );
      this.fetchedAt.set(new Date());
      return response.data;
    },
  });

  /** When the summary last arrived; null before the first read. */
  readonly loadedAt = this.fetchedAt.asReadonly();

  // `value()` throws in the error state, and the nav reads this inside a computed.
  readonly summary = computed(() =>
    this.summaryResource.hasValue()
      ? this.summaryResource.value()
      : EMPTY_SUMMARY
  );

  readonly isLoading = this.summaryResource.isLoading;

  reload() {
    this.summaryResource.reload();
  }
}
